export function subtractDate(dateInFuture: Date, dateInPast: Date): Date {
  return new Date(dateInFuture.getTime() - dateInPast.getTime());
}

export function timeAgoString(date: Date): string {
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();
  const millisecondsInSecond = 1000;
  const millisecondsInMinute = millisecondsInSecond * 60;
  const millisecondsInHour = millisecondsInMinute * 60;
  const millisecondsInDay = millisecondsInHour * 24;

  const daysAgo = Math.floor(diffInMilliseconds / millisecondsInDay);
  const hoursAgo = Math.floor((diffInMilliseconds % millisecondsInDay) / millisecondsInHour);

  const daysString = daysAgo === 1 ? 'Day' : 'Days';
  const hoursString = hoursAgo === 1 ? 'Hour' : 'Hours';

  return `${daysAgo} ${daysString}, ${hoursAgo} ${hoursString} ago`;
}

/**
 * Examples: "just now", "5 mins ago", "3 hours ago", "yesterday", "5 days ago"
 */
export function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just Now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString();
}

export function dateString(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  return date.toLocaleDateString('en-US', options);
}

/**
 * Format date as "Feb 20, 2026, 4:49 PM" (without day of week)
 */
export function formatDateTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  return date.toLocaleDateString('en-US', options);
}

export function getEndOfMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

export function getYesterday(date: Date = new Date()): Date {
  return new Date(date.getTime() - 24 * 60 * 60 * 1000);
}

export function toYearMonthDay(dateString: string, separator: string = "-"): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}${separator}${month.toString().padStart(2, "0")}${separator}${day.toString().padStart(2, "0")}`;
}

export function formatDuration(totalSeconds: number): string {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export function formatSecondsAgo(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  return `${mins}m ${seconds % 60}s ago`;
}

export function toYearMonthDayTime(dateString: string, separator: string = "-", space: string = " "): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours() % 12 || 12; // Convert to 12-hour format
  const minutes = date.getMinutes();
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  return `${year}${separator}${month.toString()}${separator}${day.toString().padStart(2, "0")}${space}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}${space}${ampm}`;
}

export function getLocalDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};